/**
 * CSV Parser Utilities
 * Requirements: 13.2 - CSV format validation
 */
import { bulkRegistrationUserSchema } from '../schemas/bulkRegistration.schema';
import type {
  CsvParseResult,
  CsvValidationError,
  BulkRegistrationUser,
} from '../types/bulkRegistration.types';

/**
 * Parse CSV string into BulkRegistrationUser array with validation
 * CSV format: email,username,name,role
 */
export function parseCSV(csvData: string): CsvParseResult {
  const errors: CsvValidationError[] = [];
  const users: BulkRegistrationUser[] = [];

  // Handle empty CSV
  if (!csvData || csvData.trim() === '') {
    return {
      users: [],
      errors: [{ row: 0, field: '', message: 'CSVファイルが空です' }],
      isValid: false,
    };
  }

  // Normalize line endings and split into lines
  const lines = csvData.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim().split('\n');

  if (lines.length < 1) {
    return {
      users: [],
      errors: [{ row: 0, field: '', message: 'CSVにヘッダー行が必要です' }],
      isValid: false,
    };
  }

  // Parse header
  const header = lines[0].toLowerCase().split(',').map((h) => h.trim());
  const emailIndex = header.indexOf('email');
  const usernameIndex = header.indexOf('username');
  const nameIndex = header.indexOf('name');
  const roleIndex = header.indexOf('role');
  const passwordIndex = header.indexOf('password');

  // Validate required columns
  if (emailIndex === -1) {
    errors.push({ row: 1, field: 'header', message: 'CSVにemail列が必要です' });
  }
  if (usernameIndex === -1) {
    errors.push({ row: 1, field: 'header', message: 'CSVにusername列が必要です' });
  }
  if (nameIndex === -1) {
    errors.push({ row: 1, field: 'header', message: 'CSVにname列が必要です' });
  }

  if (emailIndex === -1 || usernameIndex === -1 || nameIndex === -1) {
    return {
      users: [],
      errors,
      isValid: false,
    };
  }

  // Check for data rows
  if (lines.length < 2) {
    return {
      users: [],
      errors: [{ row: 0, field: '', message: 'CSVにデータ行がありません' }],
      isValid: false,
    };
  }

  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines

    const values = line.split(',').map((v) => v.trim());
    const email = values[emailIndex] || '';
    const username = values[usernameIndex] || '';
    const name = values[nameIndex] || '';
    const role = roleIndex >= 0 ? (values[roleIndex] || 'user').toLowerCase() : 'user';
    const password = passwordIndex >= 0 ? values[passwordIndex] : undefined;

    const user: BulkRegistrationUser = {
      email,
      username,
      name,
      password,
      role: role as 'user' | 'admin',
    };

    // Validate using zod schema
    const validationResult = bulkRegistrationUserSchema.safeParse(user);

    if (!validationResult.success && validationResult.error?.issues) {
      validationResult.error.issues.forEach((issue) => {
        errors.push({
          row: i + 1, // 1-indexed row number (including header)
          field: String(issue.path[0] || ''),
          message: issue.message,
        });
      });
    }

    // Also validate role separately for better error messages
    if (role && !['user', 'admin'].includes(role)) {
      errors.push({
        row: i + 1,
        field: 'role',
        message: 'roleはuserまたはadminである必要があります',
      });
    }

    users.push(user);
  }

  return {
    users,
    errors,
    isValid: errors.length === 0,
  };
}

/**
 * Generate CSV template for download
 */
export function generateCSVTemplate(): string {
  const header = 'email,username,name,role,password';
  const sampleRow = 'example@company.com,yamada_taro,山田太郎,user,Password1!';

  return `${header}\n${sampleRow}`;
}

/**
 * Convert users array back to CSV string (for API upload)
 */
export function usersToCSV(users: BulkRegistrationUser[]): string {
  const header = 'email,username,name,role,password';
  const rows = users.map((u) => `${u.email},${u.username},${u.name},${u.role || 'user'},${u.password || ''}`);
  return [header, ...rows].join('\n');
}
